/**
 * Dark mode - dark_01, for everyone on Worthly 2.0 since dark_02.
 *
 * Appearance is Auto (follows the phone), Light or Dark, kept on this phone.
 * It's dark when the appearance says so AND the screens showing are 2.0
 * ones: Worthly 2.0 is on, or you're on the welcome, sign-up and setup
 * screens (2.0 for everyone). The older themes always stay light.
 * While dark, <html> gets data-dark="1" and styles/w2-dark.css flips the
 * colours ("Midnight lilac").
 *
 * The status bar text follows too (light text on the dark page), through
 * Capacitor's StatusBar plugin. On the web it's skipped.
 */
import { useEffect, useState } from 'react';
import { registerPlugin, Capacitor } from '@capacitor/core';
import '../styles/w2-dark.css';

export const APPEARANCE_KEY = 'worthly_appearance';
export const APPEARANCE_EVENT = 'worthly:appearance';
const SCOPE_KEY = 'worthly_dark_scope';
const PAGE_DARK = '#16131D';

const StatusBar = registerPlugin('StatusBar');

export const getAppearance = () => {
  try {
    const v = window.localStorage.getItem(APPEARANCE_KEY);
    return v === 'light' || v === 'dark' ? v : 'auto';
  } catch (e) { return 'auto'; }
};

export const setAppearance = (v) => {
  try { window.localStorage.setItem(APPEARANCE_KEY, v); } catch (e) { /* private mode */ }
  window.dispatchEvent(new Event(APPEARANCE_EVENT));
};

const phoneIsDark = () => {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches); } catch (e) { return false; }
};

// Are 2.0 screens showing? Remembered on this phone, so the next launch
// starts in the right mode instead of flashing.
let scope = (() => {
  try { return window.localStorage.getItem(SCOPE_KEY) === '1'; } catch (e) { return false; }
})();
export const setDarkScope = (on) => {
  on = !!on;
  if (on === scope) return;
  scope = on;
  try { window.localStorage.setItem(SCOPE_KEY, on ? '1' : '0'); } catch (e) { /* private mode */ }
  applyDark();
};

let lastBar = null;
const setStatusBar = (dark) => {
  if (lastBar === dark) return;
  lastBar = dark;
  try {
    if (!Capacitor.isNativePlatform()) return;
    StatusBar.setStyle({ style: dark ? 'DARK' : 'LIGHT' }).catch(() => {});
  } catch (e) { /* plugin not installed */ }
};

export const shouldBeDark = () => scope
  && (getAppearance() === 'dark' || (getAppearance() === 'auto' && phoneIsDark()));

export const applyDark = () => {
  const el = document.documentElement;
  const dark = shouldBeDark();
  if (dark) {
    el.setAttribute('data-dark', '1');
    el.classList.remove('force-light');
    el.style.colorScheme = 'dark';
  } else {
    el.removeAttribute('data-dark');
    el.classList.add('force-light');
    el.style.colorScheme = 'light';
  }
  let meta = document.querySelector('meta[name="theme-color"]');
  if (dark) {
    if (!meta) { meta = document.createElement('meta'); meta.name = 'theme-color'; document.head.appendChild(meta); }
    if (!meta.dataset.light) meta.dataset.light = meta.content || '';
    meta.content = PAGE_DARK;
  } else if (meta && meta.dataset.light !== undefined) {
    meta.content = meta.dataset.light;
  }
  setStatusBar(dark);
  return dark;
};

// Follow the phone and the Settings choice.
let watching = false;
const watch = () => {
  if (watching) return;
  watching = true;
  try {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    if (mq.addEventListener) mq.addEventListener('change', applyDark);
    else if (mq.addListener) mq.addListener(applyDark);
  } catch (e) { /* no matchMedia */ }
  window.addEventListener(APPEARANCE_EVENT, applyDark);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) { lastBar = null; applyDark(); } });
};

// App.js: which screens are showing. `on` = 2.0 screens, `known` = false while still loading.
export const useDarkScope = (on, known = true) => {
  useEffect(() => {
    if (known) setDarkScope(on);
  }, [on, known]);
};

// The Settings rows: 'auto' | 'light' | 'dark'.
export const useAppearance = () => {
  const [v, setV] = useState(getAppearance);
  useEffect(() => {
    const sync = () => setV(getAppearance());
    window.addEventListener(APPEARANCE_EVENT, sync);
    return () => window.removeEventListener(APPEARANCE_EVENT, sync);
  }, []);
  return [v, setAppearance];
};

// Straight away at launch, from what this phone remembers.
try { watch(); applyDark(); } catch (e) { /* not in a browser */ }
